import { AiOutlineLoading } from "react-icons/ai";
import {
  Alert,
  Badge,
  Button,
  Card,
  Checkbox,
  Label,
  List,
  Tabs,
  Timeline,
} from "flowbite-react";
import { ADMIN, PAYMENTS } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { MdOutlineAttachMoney } from "react-icons/md";
import { BsFillBarChartFill } from "react-icons/bs";
import { formatToReal } from "../../utils/utils";
import { useQuery } from "react-query";
import axiosInstance from "../../config/axiosInstance";
import DrawerLoading from "../DrawerLoading";
import CurrencyInput from "../inputs/CurrencyInput";
import { useForm } from "react-hook-form";
import Input from "../inputs/Input";
import { MdHistory } from "react-icons/md";
import PaymentHistoryModal from "../modals/PaymentHistoryModal";

const CompanyInvoiceForm = ({
  company,
  isDrawerOpen,
  onClose,
  companyType = "REPRESENTANTE",
}) => {
  const tabsRef = useRef(null);
  const [startDate, setStartDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [activeTab, setActiveTab] = useState(0);
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalPriceSumBase, setTotalPriceSumBase] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);
  const [, setKey] = useState(0);
  const [errorPayment, setErrorPayment] = useState(false);
  const [openPaymentHistoryModal, setOpenPaymentHistoryModal] =
    useState(false);
  const [selectedPaymentForHistory, setSelectedPaymentForHistory] =
    useState(null);

  const {
    isLoading: isLoadingInfo,
    error: errorInfo,
    data: info,
  } = useQuery(
    ["info", startDate, endDate, currentPage, company],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_COMPANY_QUERIES_HISTORY_SIMPLIFIED}/${company?.id}?startDate=${startDate}&endDate=${endDate}&page=${currentPage}`
      );

      setTotalPriceSum(data.data.totalSumPrice || 0);
      setTotalPriceSumBase(data.data.totalPriceSumBase || 0);
      setTotalCount(data.data.totalSumCount);
      setTotalCustomers(data.data.totalCustomers || 0);

      return data.data.data;
    },
    {
      enabled: isDrawerOpen,
    }
  );

  const {
    isLoading: isLoadingInvoices,
    error: errorInvoices,
    data: invoices,
  } = useQuery(
    ["invoices", startDate, endDate, company],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_HISTORY_INVOICES}/${company?.id}?startDate=${startDate}&endDate=${endDate}`
      );

      return data.data;
    },
    {
      enabled: isDrawerOpen,
    }
  );

  const {
    register,
    reset,
    setValue,
    watch,
    control,
    formState: { errors },
    clearErrors,
  } = useForm();

  const handleCreatePayment = async () => {
    try {
      setIsSubmittingPayment(true);
      setErrorPayment(false);
      const { data } = await axiosInstance.get(
        `${PAYMENTS.CREATE_PAYMENT_ADMIN}?value=${watch(
          "value"
        )}&type=BOLETO&companyId=${company?.id}&expirationDate=${watch("date")}`
      );

      window.open(data.data.paymentLink, "_blank");
    } catch (error) {
      setErrorPayment(true);
    } finally {
      setIsSubmittingPayment(false);
    }
  };

  const openPaymentHistory = (invoice) => {
    setSelectedPaymentForHistory(invoice);
    setOpenPaymentHistoryModal(true);
  };

  useEffect(() => {
    reset();
    setErrorPayment(false);
    setValue("date", moment().format("yyyy-MM-DD"));
  }, [company]);

  return (
    <>
      <div className="font-semibold mb-4 title-syncx">
        {companyType === "REPRESENTANTE" ? "Representante" : "Cliente"}:{" "}
        {company?.name || company?.socialReason}
      </div>
      <div className="font-semibold text-sm mb-2">Período</div>
      <div className="flex items-center gap-1 mb-5">
        <input
          type="date"
          value={startDate}
          onChange={(e) => {
            setStartDate(e.target.value);
            if (moment(e.target.value).isAfter(moment(endDate))) {
              setEndDate(e.target.value);
            }
          }}
          className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
        />
        <div className="text-xs">Até</div>
        <input
          type="date"
          value={endDate}
          onChange={(e) => {
            setEndDate(e.target.value);
          }}
          min={startDate}
          className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 appearance-none dark:text-gray-400 dark:border-gray-700 focus:outline-none focus:ring-0 focus:border-gray-200 peer"
        />
      </div>
      <div className="italic text-sm mb-2 flex items-center gap-2">
        Informações baseadas no período de{" "}
        <Badge className="w-fit">
          {moment(startDate).format("DD/MM/yyyy")}
        </Badge>{" "}
        até{" "}
        <Badge className="w-fit"> {moment(endDate).format("DD/MM/yyyy")}</Badge>
      </div>
      <Tabs
        aria-label="Tabs with underline"
        variant="underline"
        ref={tabsRef}
        onActiveTabChange={(tab) => setActiveTab(tab)}
      >
        <Tabs.Item active title="Resumo">
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              {companyType === "REPRESENTANTE" && (
                <Card
                  className="w-full bg-gray-700 relative"
                  style={{ minWidth: "150px" }}
                >
                  <div className="text-sm md:text-md lg:text-base text-white">
                    Valor Total a pagar:
                  </div>
                  <div className="text-2xl text-white font-semibold flex gap-2 items-center">
                    {!isLoadingInfo ? (
                      `${formatToReal(totalPriceSum)}`
                    ) : (
                      <div
                        role="status"
                        className="max-w-sm animate-pulse inline"
                      >
                        <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                      </div>
                    )}
                  </div>
                  <MdOutlineAttachMoney className="absolute bottom-0 right-0 text-white text-9xl opacity-10" />
                </Card>
              )}

              <Card className="w-full bg-gray-700 relative overflow-hidden">
                <div className="text-sm md:text-md lg:text-base text-white">
                  Valor Total a receber:
                </div>
                <div className="text-2xl text-white font-semibold">
                  {!isLoadingInfo ? (
                    `${formatToReal(totalPriceSumBase)}`
                  ) : (
                    <div
                      role="status"
                      className="max-w-sm animate-pulse inline"
                    >
                      <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                    </div>
                  )}
                </div>
                <MdOutlineAttachMoney className="absolute bottom-0 right-0 text-white text-9xl opacity-10" />
              </Card>
              <Card className="w-full bg-gray-700 relative overflow-hidden">
                <div className="text-sm md:text-md lg:text-base text-white">
                  Total de consultas:
                </div>
                <div className="text-2xl text-white font-semibold">
                  {!isLoadingInfo ? (
                    `${totalCount}`
                  ) : (
                    <div
                      role="status"
                      className="max-w-sm animate-pulse inline"
                    >
                      <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                    </div>
                  )}
                </div>
                <BsFillBarChartFill className="absolute bottom-0 right-2 text-white text-9xl opacity-10" />
              </Card>
              {companyType === "REPRESENTANTE" && (
                <Card className="w-full bg-gray-700 relative overflow-hidden">
                  <div className="text-sm md:text-md lg:text-base text-white">
                    Total de clientes:
                  </div>
                  <div className="text-2xl text-white font-semibold">
                    {!isLoadingInfo ? (
                      `${totalCustomers}`
                    ) : (
                      <div
                        role="status"
                        className="max-w-sm animate-pulse inline"
                      >
                        <div className="h-7 bg-gray-200 rounded-full dark:bg-gray-700 w-40"></div>
                      </div>
                    )}
                  </div>
                  <BsFillBarChartFill className="absolute bottom-0 right-2 text-white text-9xl opacity-10" />
                </Card>
              )}
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <div className="title-syncx">Tipo de pagamento: Boleto</div>
              <div className="mt-3">
                <CurrencyInput
                  control={control}
                  register={register}
                  name={`value`}
                  className="w-30"
                  label="Valor"
                />
              </div>
              <div className="mt-2">
                <Input
                  type="date"
                  register={register}
                  name="date"
                  label="Data de vencimento:"
                />
              </div>
              <Button
                className="relative z-10"
                disabled={watch("value") < 5 || isSubmittingPayment}
                isProcessing={isSubmittingPayment}
                onClick={handleCreatePayment}
              >
                Gerar cobrança
              </Button>
              <small className="text-center -mt-1">
                Só é possível gerar uma cobrança se o valor for maior que R$5
              </small>
              {errorPayment && (
                <Alert withBorderAccent color="failure">
                  Um erro ocorreu ao tentar gerar o pagamento
                </Alert>
              )}
            </div>
          </div>
        </Tabs.Item>
        <Tabs.Item title="Detalhes">
          {info?.length > 0 ? (
            <List
              unstyled
              className="w-full divide-y divide-gray-200 dark:divide-gray-700"
            >
              {info?.map((item, index) => (
                <List.Item className="py-2 text-sm" key={index}>
                  <div className="flex justify-between gap-3 items-center">
                    <div>
                      <div className="font-semibold">{item.queryName}</div>
                      <div>Quantidade: {item.count}</div>
                    </div>
                    <div>{formatToReal(item.totalPrice || 0)}</div>
                  </div>
                </List.Item>
              ))}
            </List>
          ) : (
            <Alert color="gray" withBorderAccent>
              Nada registrado para este período!
            </Alert>
          )}
        </Tabs.Item>
        <Tabs.Item title="Histórico">
          {invoices?.length > 0 ? (
            <Timeline>
              {invoices?.map((invoice, index) => (
                <Timeline.Item key={index}>
                  <Timeline.Point />
                  <Timeline.Content>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <Timeline.Time>
                          Data: {moment(invoice?.createdAt).format("DD/MM/yyyy")}
                        </Timeline.Time>
                        <Timeline.Title>
                          Valor: {formatToReal(invoice?.price)}
                        </Timeline.Title>
                      </div>
                      <Button
                        size="xs"
                        color="light"
                        className="shrink-0"
                        onClick={() => openPaymentHistory(invoice)}
                      >
                        <MdHistory className="mr-2" />
                        Histórico
                      </Button>
                    </div>
                    <Timeline.Body className="text-sm">
                      <div className="flex items-center gap-1">
                        <div className="font-semibold">Tipo:</div>
                        <div>{invoice?.type}</div>
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <div className="font-semibold">Status:</div>
                        <div>
                          {invoice.isPaid ? (
                            <Badge color="green">Pago</Badge>
                          ) : (
                            <Badge color="warning">Pendente</Badge>
                          )}
                        </div>
                      </div>
                      {!invoice.isPaid && (
                        <div className="flex items-center gap-1 mt-1">
                          <div className="font-semibold">
                            Link de pagamento:
                          </div>
                          <a
                            className="underline"
                            href={invoice.paymentLink}
                            target="_blank"
                          >
                            {invoice.paymentLink}
                          </a>
                        </div>
                      )}
                    </Timeline.Body>
                  </Timeline.Content>
                </Timeline.Item>
              ))}
            </Timeline>
          ) : (
            <Alert color="gray" withBorderAccent>
              Nada registrado para este período!
            </Alert>
          )}
        </Tabs.Item>
      </Tabs>
      <PaymentHistoryModal
        open={openPaymentHistoryModal}
        onClose={() => {
          setOpenPaymentHistoryModal(false);
          setSelectedPaymentForHistory(null);
        }}
        payment={selectedPaymentForHistory}
        companyId={company?.id}
        allowComment={false}
        source="customer_panel"
      />
      {isLoadingInfo || (isLoadingInvoices && <DrawerLoading />)}
    </>
  );
};

export default CompanyInvoiceForm;
